import type { PhoneNumber } from "libphonenumber-js";
import parsePhoneNumber from "libphonenumber-js";

export const parsePhone = (value: string | null | undefined): PhoneNumber | undefined => {
  return value ? parsePhoneNumber(value, "US") : undefined;
};

export const isValidPhone = (value: string): boolean => {
  return !!parsePhone(value)?.isPossible();
};

export const formatE164 = (value: string | null | undefined): string | undefined => {
  if (!value?.trim()) return;

  const phone = parsePhone(value);
  if (!phone) return value;

  return phone.format("E.164");
};

export const formatPhoneNumber = (value: string | null | undefined): string | undefined => {
  if (!value?.trim()) return;

  const phone = parsePhone(value);
  if (!phone) return value; // If it doesn't parse, return the original value to at least show something

  if (phone.countryCallingCode === "1") {
    return phone.formatNational();
  } else {
    return phone.formatInternational();
  }
};

export const formatPhoneDigitsOnlyNoCountry = (value: string | null | undefined): string | undefined => {
  if (!value?.trim()) return;

  const phone = parsePhone(value);

  return phone?.nationalNumber ?? value;
};

export type SmsPhonePreference = "personal" | "work";

type ResolveSmsPhoneInput = {
  teamMember: {
    phone?: string | null;
    work_phone?: string | null;
    sms_phone_preference: SmsPhonePreference;
  };
};

export type ResolvedSmsPhone = {
  number: string;
  source: SmsPhonePreference;
  /** True when the caller's preferred number was missing and we fell back to the other one. */
  fellBack: boolean;
};

/**
 * Resolve which phone number to send an SMS to for a given team member.
 *
 * If the preferred phone is missing, falls back to whichever phone is populated.
 * Returns `null` when neither `phone` nor `work_phone` is populated.
 */
export const resolveSmsPhoneForTeamMember = ({
  teamMember,
}: ResolveSmsPhoneInput): ResolvedSmsPhone | null => {
  const preference = teamMember.sms_phone_preference;
  const preferredNumber = preference === "work" ? teamMember.work_phone : teamMember.phone;
  if (preferredNumber) {
    return { number: preferredNumber, source: preference, fellBack: false };
  }

  const otherSource: SmsPhonePreference = preference === "work" ? "personal" : "work";
  const otherNumber = otherSource === "work" ? teamMember.work_phone : teamMember.phone;
  if (otherNumber) {
    return { number: otherNumber, source: otherSource, fellBack: true };
  }

  return null;
};
